import {
  listCierreIds, loadCierre, updateCierreStatus, markFinishDone, clearCierre,
} from 'utils/offlineCierreStore';
import { hasDraft } from 'utils/offlineMantencionStore';
import {
  loadSignaturesForMantencion, deleteSignaturesForMantencion,
} from 'utils/offlineSignaturesStore';
import { presignSignatureUpload } from 'api/signatures';
import { request } from 'hooks/useAppApi';
import { sendMantencionPdf } from 'PDF/Mantencion/sendMantencionPdf';
import { getAccessTokenApi, getRefreshTokenApi, refreshAccessTokenApi } from 'api/auth';

// Sube las firmas del cierre offline (encolado por Task 6) y envía el informe
// al reconectar. Gemelo de `offlineMantencionSync.flushPendingDrafts` pero
// para el cierre (finish + envío), no el checklist. Idempotente y
// reentrante-seguro (guard `inFlight`).
let inFlight = false;

const isOk = (res) =>
  !!res && typeof res.status === 'number' && res.status >= 200 && res.status < 300;

// Motivos legibles para el técnico (español neutral, sin tecnicismos).
function motivoLegible(res) {
  if (res && res.status === 409) return 'Faltan resolver hallazgos de una visita anterior.';
  if (res && res.status === 400) return 'Faltan datos para cerrar la mantención.';
  return 'No se pudo cerrar la mantención. Vuelve a intentarlo.';
}

// Renueva el access token si venció, usando el refresh. Devuelve false si no hay
// forma de autenticar (refresh vencido) → el cierre se marca error de sesión.
async function asegurarToken() {
  if (getAccessTokenApi()) return true;
  const rt = getRefreshTokenApi();
  if (!rt) return false;
  return (await refreshAccessTokenApi(rt).catch(() => false)) === true;
}

// Resuelve la key S3 de una firma: si ya es key S3 la devuelve; si es un localId,
// sube el blob desde IndexedDB. Devuelve la key o null si no pudo.
async function resolverFirmaKey(ref, firmasLocales) {
  if (typeof ref === 'string' && ref.startsWith('signatures/')) return ref;
  const local = firmasLocales.find((f) => f.localId === ref);
  if (!local) return null;
  const { uploadUrl, key } = await presignSignatureUpload();
  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'image/png' },
    body: local.blob,
  });
  if (!putRes || !putRes.ok) throw new Error('S3 PUT firma falló');
  return key;
}

// Recorre los cierres encolados y los sube: primero `finish` (si no se hizo
// aún) y luego el envío del informe PDF. Un `finish` con 4xx (rechazo del
// backend) es terminal → ERROR con motivo legible; un 5xx o una excepción
// (red/S3) es transitorio → se conserva PENDIENTE para el próximo reconectar.
export async function flushPendingCierres() {
  if (inFlight) return { synced: [], errored: [], skipped: true };
  inFlight = true;
  const synced = [];
  const errored = [];
  try {
    for (const id of listCierreIds()) {
      const cierre = loadCierre(id);
      if (!cierre) continue;
      // El checklist debe estar sincronizado antes de cerrar.
      if (hasDraft(id)) continue; // reintento al próximo online

      try {
        // eslint-disable-next-line no-await-in-loop
        const auth = await asegurarToken();
        if (!auth) {
          updateCierreStatus(id, 'ERROR', 'Debes iniciar sesión nuevamente para enviar esta mantención.');
          errored.push(id);
          continue;
        }
        updateCierreStatus(id, 'ENVIANDO', null);

        if (!cierre.finishDone) {
          // eslint-disable-next-line no-await-in-loop
          const firmasLocales = await loadSignaturesForMantencion(id);
          // eslint-disable-next-line no-await-in-loop
          const firmaSpecKey = await resolverFirmaKey(cierre.firmaSpecRef, firmasLocales);
          // eslint-disable-next-line no-await-in-loop
          const firmaRecepKey = await resolverFirmaKey(cierre.firmaRecepRef, firmasLocales);
          // eslint-disable-next-line no-await-in-loop
          const finishRes = await request('/mantenciones/finish', 'post', {
            id_mantencion: id,
            firma_spec: firmaSpecKey,
            firma_recep: firmaRecepKey,
            name_recep: cierre.name_recep,
            cargo_recep: cierre.cargo_recep,
            fecha_finalizacion: cierre.fecha_finalizacion,
            lat: cierre.lat,
            lng: cierre.lng,
          });
          if (!isOk(finishRes)) {
            // 4xx = rechazo terminal; 5xx/red = transitorio (deja PENDIENTE, sin marca dura)
            const status = finishRes && finishRes.status;
            if (status >= 400 && status < 500) {
              updateCierreStatus(id, 'ERROR', motivoLegible(finishRes));
              errored.push(id);
            } else {
              updateCierreStatus(id, 'PENDIENTE', null); // transitorio
            }
            continue;
          }
          markFinishDone(id);
        }

        // eslint-disable-next-line no-await-in-loop
        await sendMantencionPdf(id);
        // eslint-disable-next-line no-await-in-loop
        await deleteSignaturesForMantencion(id);
        clearCierre(id);
        synced.push(id);
      } catch {
        // Excepción (red/S3/PDF) = transitorio → conservar como PENDIENTE.
        updateCierreStatus(id, 'PENDIENTE', null);
      }
    }
  } finally {
    inFlight = false;
  }
  return { synced, errored };
}
